import type { ChecklistItem, TimelineBoardKey } from '../../../types/schema'
import type { AiContextDigest, AiOperationTargetRef } from '../../../types/aiOperational'
import { buildOperationalRetrievalContext } from '../retrieval/retrievalEngine'
import type { AiRankedRetrievalRecord, AiRetrievalRecord } from '../retrieval/retrievalTypes'

export interface AiRetrievalElement {
  id: string
  workspaceId: string
  type: AiOperationTargetRef['type']
  title: string
  description?: string | null
  parentId: string | null
  board?: TimelineBoardKey
  boardStatus?: string | null
  workflowStatus?: string | null
  progress?: number
  dependencyNodeIds?: string[]
  startDate?: string | null
  endDate?: string | null
}

export interface AiRetrievalInput {
  workspaceId: string
  query: string
  board?: TimelineBoardKey
  selectedTargets?: AiOperationTargetRef[]
  elements: AiRetrievalElement[]
  checklistItems: ChecklistItem[]
  pulseHints?: Array<{ id: string; summary: string; severity?: string; targetId?: string }>
  supplementalRecords?: AiRetrievalRecord[]
  maxPrimaryRecords?: number
  maxSupportingRecords?: number
}

export interface AiRetrievedRecord {
  id: string
  type: AiOperationTargetRef['type']
  title: string
  score: number
  reasons: string[]
  parentId?: string | null
  board?: TimelineBoardKey
  status?: string | null
  progress?: number
  dueDate?: string | null
}

export interface AiOperationalContextWindow {
  workspaceId: string
  query: string
  primary: AiRetrievedRecord[]
  supporting: AiRetrievedRecord[]
  pulseHints: Array<{ id: string; summary: string; severity?: string; targetId?: string }>
  digest: AiContextDigest
}

const legacyReasons = (record: AiRankedRetrievalRecord) => {
  const reasons = record.ranking.factors.map((factor) => factor.reason)
  if ((record.dependencyIds ?? []).length > 0 || (record.blockerIds ?? []).length > 0) reasons.push('dependency participant')
  if ((record.riskSignals ?? []).some((signal) => /proof|location|co-sign|completion/i.test(signal))) reasons.push('completion gate')
  if (record.board) reasons.push('same board')
  if (/in_progress|ongoing/i.test(`${record.status ?? ''} ${record.workflowStatus ?? ''}`)) reasons.push('active work')
  return reasons.filter((reason, index, all) => all.indexOf(reason) === index)
}

const toLegacyRecord = (record: AiRankedRetrievalRecord): AiRetrievedRecord => {
  const type = record.targetRef?.type ?? (record.type === 'ACTIVITY' || record.type === 'UPLOAD' || record.type === 'BUDGET' || record.type === 'STOCK' || record.type === 'PULSE' || record.type === 'MEMORY' || record.type === 'PROPOSAL' || record.type === 'UNKNOWN'
    ? 'WORKSPACE'
    : record.type)
  return {
    id: record.id,
    type,
    title: record.title,
    score: record.ranking.score,
    reasons: legacyReasons(record),
    parentId: record.parentId,
    board: record.board,
    status: record.status ?? record.workflowStatus ?? null,
    progress: record.progress,
    dueDate: record.dueDateIso ?? null,
  }
}

export const retrieveOperationalContext = (input: AiRetrievalInput): AiOperationalContextWindow => {
  const maxPrimary = input.maxPrimaryRecords ?? 8
  const maxSupporting = input.maxSupportingRecords ?? 12
  const bundle = buildOperationalRetrievalContext({
    workspaceId: input.workspaceId,
    query: input.query,
    activeBoard: input.board,
    selectedTargets: input.selectedTargets,
    elements: input.elements,
    checklistItems: input.checklistItems,
    pulseHints: input.pulseHints,
    supplementalRecords: input.supplementalRecords,
    limits: {
      maxRecords: maxPrimary + maxSupporting,
      maxSlices: 8,
      maxCharacters: 4_800,
    },
  })
  const legacyRecords = bundle.rankedRecords.map(toLegacyRecord)

  return {
    workspaceId: input.workspaceId,
    query: input.query,
    primary: legacyRecords.slice(0, maxPrimary),
    supporting: legacyRecords.slice(maxPrimary, maxPrimary + maxSupporting),
    pulseHints: input.pulseHints ?? [],
    digest: bundle.digest,
  }
}
